import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { FingerprintIcon, KeyIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { isWebAuthnSupported } from 'utils/webAuthn';

import { submitPasskeySignInAction } from 'store/auth/actions';

import { LoginTestIds } from '../../Login.testIds';

const passkeyIcon = <FingerprintIcon />;
const passwordIcon = <KeyIcon />;

const PasskeyLoginTab = ({ onFallbackToPassword }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const webAuthnSupported = isWebAuthnSupported();

  const onPasskeySignIn = useCallback(async () => {
    setIsSubmitting(true);
    setHasError(false);

    try {
      // Discoverable (usernameless) assertion: getPasskeyAssertionOptions takes no username, the
      // authenticator surfaces the user's passkeys, and the backend resolves the user from the
      // credential's userHandle — so there is nothing to type. Fired only on an explicit click.
      await dispatch(submitPasskeySignInAction());
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch]);

  if (!webAuthnSupported) {
    return (
      <div className="flex flex-col w-full">
        <Typography className="mb-16 text-center" color="text.secondary" variant="medium14">
          Your browser does not support passkeys. Sign in with your username and password.
        </Typography>

        {onFallbackToPassword && (
          <Button
            fullWidth
            className="normal-case"
            color="primary"
            data-testid={LoginTestIds.PASSKEY_USE_PASSWORD_BUTTON}
            size="large"
            startIcon={passwordIcon}
            variant="contained"
            onClick={onFallbackToPassword}
          >
            Use username and password
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <Typography className="mb-16 text-center" color="text.secondary" variant="medium14">
        Use your passkey — fingerprint, face ID, or security key.
      </Typography>

      {hasError && (
        <Typography
          className="mb-16 text-center"
          color="error.main"
          data-testid={LoginTestIds.PASSKEY_ERROR_MESSAGE}
          variant="medium14"
        >
          Passkey sign-in was cancelled. Try again, or use your username and password.
        </Typography>
      )}

      <Button
        fullWidth
        aria-label="SIGN IN WITH PASSKEY"
        className="normal-case"
        color="primary"
        data-testid={LoginTestIds.PASSKEY_SIGN_IN_BUTTON}
        disabled={isSubmitting}
        size="large"
        startIcon={passkeyIcon}
        variant="contained"
        onClick={onPasskeySignIn}
      >
        Sign in with passkey
      </Button>

      {onFallbackToPassword && (
        <Button
          fullWidth
          className="mt-12 normal-case"
          color="primary"
          data-testid={LoginTestIds.PASSKEY_USE_PASSWORD_BUTTON}
          disabled={isSubmitting}
          size="large"
          startIcon={passwordIcon}
          variant="outlined"
          onClick={onFallbackToPassword}
        >
          Use username and password
        </Button>
      )}
    </div>
  );
};

PasskeyLoginTab.propTypes = {
  onFallbackToPassword: PropTypes.func,
};

export default PasskeyLoginTab;
